import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Spinner, Alert, Button, Accordion } from "react-bootstrap";
import MainLayout from "core/components/layout/MainLayout";
import BreadcrumbWidget from "core/components/shared/BreadcrumbWidget";
import ReactMarkdown from "react-markdown";
import PromptManagerWidget from "../components/PromptManagerWidget";
import CustomModal from "core/components/shared/CustomModal";
import StreamView from "../components/StreamView";
import SelectTheme from "../components/SelectTheme";
import PusherListener from "features/feature_chat/components/PusherListener";
import UseCasesWidget from '../components/UseCasesWidget';
import HelpAccordion from '../components/HelpAccordion';
import { ReportGenerationHelpText } from '../constants/helpTexts';
import { ContentHttpService } from "../services";

interface ReportFormData {
  query: string;
  report_length: string;
  prompt_id?: number;
  theme_id: number | null;
  theme_is_featured: boolean;
}

interface StreamResponse {
  stream_id: string;
  report_id?: number;
  thoughts_channel?: string;
  status_channel?: string;
  report_channel?: string;
}

const ReportGenerationPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ReportFormData>({
    query: "",
    report_length: "detailed",
    prompt_id: undefined,
    theme_id: null,
    theme_is_featured: false
  });
  const [selectedPrompt, setSelectedPrompt] = useState<{ id: number; title: string; content: string } | null>(null);
  const [thoughts, setThoughts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [reportId, setReportId] = useState<number | null>(null);
  const [currentThought, setCurrentThought] = useState<string>("");
  const [useCases, setUseCases] = useState<any[]>([]);
  const [currentTheme, setCurrentTheme] = useState<{ id: number; title: string } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsGenerating(true);
    setError(null);
    setThoughts([]);
    setReportId(null);
    setCurrentTheme(null);

    try {
      const response = await ContentHttpService.generateReport({
        ...formData,
        prompt_id: selectedPrompt?.id,
      });

      if (response.id) {
        setCurrentTheme({
          id: response.id,
          title: response.title
        });
        setReportId(response.report_id);
        setIsLoading(false); // Stop the initial loading state
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate report");
      setIsGenerating(false);
      setIsLoading(false);
    }
  };

  const _reset = () => {
    setIsGenerating(false);
    setCurrentTheme(null);
    setUseCases([]);
    setCurrentThought("");
    setThoughts([]);
    setReportId(null);
  };


  const handleStopGeneration = async () => {
    if (!currentTheme?.id) return;
    
    try {
      await ContentHttpService.stopReportGeneration(currentTheme.id);
      _reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop report generation");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePromptSelect = (prompt: { id: number; title: string; content: string }) => {
    setSelectedPrompt(prompt);
    setFormData(prev => ({ ...prev, query: prompt.content }));
  };

  const handleThoughtReceived = (thought: string) => {
    setThoughts([...thoughts, thought]);
    setCurrentThought(thought);
  };

  const handleUseCaseReceived = (useCase: any) => {
    console.log('Raw use case received:', useCase);
    
    // Ensure we have a valid use case object
    if (!useCase || typeof useCase !== 'object') {
      console.error('Invalid use case received:', useCase);
      return;
    }

    // If the use case is a string, try to parse it
    if (typeof useCase === 'string') {
      try {
        useCase = JSON.parse(useCase);
      } catch (e) {
        console.error('Failed to parse use case string:', e);
        return;
      }
    }

    console.log('Processed use case:', useCase);
    setUseCases(prev => [...prev, useCase]);
  };

  const handleProgress = (progress: number) => {
    if(progress!=undefined) {
      setProgress(progress);
    }
    else {
      setProgress(progress+3);
    }
  };

  return (
    <MainLayout>
      <div className="container">
        
        <BreadcrumbWidget
          mainTitle="Search Use case"
          breadcrumbs={[
            { title: "Dashboard", url: "/" },
            { title: "Use Case Library", url: "/usecases" },
            { title: "Search Use Case" },
          ]}
        />

        <div className="row">
          <div className="col-xl-12">
            <HelpAccordion helpText={ReportGenerationHelpText} />
          </div>
          <div className="col-xl-12">
            <Card className="mb-4">
              
              <CardBody>
                <form onSubmit={handleSubmit}>
                  <div className="position-relative">
                    
                    <div className="mb-3 d-flex justify-content-between">
                      <div style={{width: "calc(100%)"}}>
                        {selectedPrompt ? (
                          <div className="selected-prompt">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0">{selectedPrompt.title}</h6>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  setSelectedPrompt(null);
                                  setFormData(prev => ({ ...prev, query: "" }));
                                }}
                              >
                                <i className="bi bi-x"></i>
                              </Button>
                            </div>
                            <div className="form-control bg-light" style={{ minHeight: "100px" }}>
                              {selectedPrompt.content}
                            </div>
                          </div>
                        ) : (
                          <PromptManagerWidget
                            value={formData.query}
                            onChange={(value) => setFormData(prev => ({ ...prev, query: value }))}
                            onPromptSelect={handlePromptSelect}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <div className="form-check form-switch d-flex pt-2 gap-2">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="themeIsFeaturedSwitch" 
                        checked={formData.theme_is_featured || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, theme_is_featured: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="themeIsFeaturedSwitch">
                        Save as Featured Theme
                      </label>
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isLoading || !formData.query}
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Searching...
                        </>
                      ) : (
                        "Search Use Cases"
                      )}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>

            

            {currentTheme && (
              <>
                <Card className="mb-4">
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">Searching Use Cases for Theme: {currentTheme.title}</h5>
                      <div className="d-flex gap-2">
                        {progress!=undefined && (
                          <div className="mb-3">
                            <div className="progress" style={{ height: '8px' }}>
                              <div className="progress-bar progress-bar-striped progress-bar-animated bg-info" style={{ width: `${progress}%` }}></div>
                            </div>
                          </div>
                        )}
                        {isGenerating && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleStopGeneration}
                          >
                            Stop Generation
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/usecases/${currentTheme.id}/${currentTheme.title.replace(/\s+/g, '-')}`)}
                        >
                          View Theme Page
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    {(isGenerating && !currentThought) && (
                      <div className="alert alert-info">
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Searching for use cases... This may take a few minutes.
                      </div>
                    )}

                    <UseCasesWidget useCases={useCases} />

                    {currentThought && (
                      <div className="alert alert-info mt-3" role="alert">
                        {currentThought}
                      </div>
                    )}
                  </CardBody>
                </Card>

                <PusherListener 
                  streamKey={"use_cases_"+currentTheme?.id} 
                  onThoughtReceived={handleThoughtReceived}
                  onProgress={handleProgress}
                  onUseCaseReceived={handleUseCaseReceived}
                />
              </>
            )}

            

            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            {thoughts && thoughts.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">Generation Thoughts & Steps</h5>
                </CardHeader>
                <CardBody>
                  <div className="thoughts-container" style={{ maxHeight: "300px", overflowY: "auto" }}>
                    
                    {Array.isArray(thoughts) ? (
                     thoughts.map((thought: string, index: number) => (
                      <div key={index} className="mb-3">
                        <div className="d-flex align-items-start">
                          <div className="me-2">
                            <i className="bi bi-lightbulb text-warning"></i>
                          </div>
                          <div><ReactMarkdown>{thought}</ReactMarkdown></div>
                        </div>
                      </div>
                    ))
                    ) : (
                      <ReactMarkdown>{thoughts}</ReactMarkdown>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        title="Options"
        showFooter={false}
      >
        <div className="mb-3">
          
        </div>
        <div className="d-flex justify-content-end gap-2">
          <Button variant="outline-secondary" onClick={() => setIsSettingsModalOpen(false)}>
            Close
          </Button>
        </div>
      </CustomModal>
    </MainLayout>
  );
};

export default ReportGenerationPage; 